import {
  ActionLayout,
  Button,
  Checkbox,
  ExternalLink,
  List,
  ListItem,
  Typography,
} from '@alice-financial/pretext-ui'
import { QueryKey } from '@tanstack/react-query'
import * as React from 'react'
import { ACH_ALLOWLIST } from './achAllowList'
import { useAcceptACHMandate } from './useAcceptACHMandate'
import { ConfirmBankAccountSetupResponse } from './useConfirmBankAccount'

type ACHMandateProps<TResponse = unknown> = {
  type: 'billing' | 'banking'
  handleConfirmationResponse: (response: ConfirmBankAccountSetupResponse) => TResponse
  clientSecret: string
  invalidationKeys: Array<QueryKey>
  willBeFirstPaymentMethod?: boolean
}

/**
 * This component display a mandatory agreement for ACH payments - Stripe must be notified that the account
 * keyed by client secret has accepted the ACH mandate, and then the response is handled by the provided
 * callback, which usually instantiates a corresponding payment method in our back end.
 *
 * Note that the invalidationKeys are required because the clientSecret will no longer be usable after
 * the ACH mandate is accepted
 *
 * If `willBeFirstPaymentMethod` is true and `type === 'banking'`, we'll want to mention the initial
 * reserve deposit, otherwise we should not show that.
 */
export const ConfirmStripeACHConnection = ({
  type,
  clientSecret,
  handleConfirmationResponse,
  invalidationKeys,
  willBeFirstPaymentMethod = false,
}: ACHMandateProps) => {
  const { mutate: acceptAndPersist } = useAcceptACHMandate(handleConfirmationResponse, invalidationKeys)
  const onClick = () => acceptAndPersist({ clientSecret })
  return (
    <ConfirmACHConnection
      onAccept={onClick}
      type={type}
      willBeFirstPaymentMethod={willBeFirstPaymentMethod}
    />
  )
}

type ACHMandateSteps = 'confirm-ach-allowlist' | 'authorize-ach-transfer' | 'confirm-ach-mandate'
export const ConfirmACHConnection = ({
  onAccept,
  type,
  willBeFirstPaymentMethod = false,
}: {
  onAccept: () => void
  type: 'billing' | 'banking'
  willBeFirstPaymentMethod?: boolean
}) => {
  const [step, setStep] = React.useState<ACHMandateSteps>('confirm-ach-allowlist')
  if (step === 'confirm-ach-allowlist') {
    const nextStep =
      type === 'billing'
        ? 'confirm-ach-mandate'
        : willBeFirstPaymentMethod
          ? 'authorize-ach-transfer'
          : 'confirm-ach-mandate'
    return <ConfirmACHAllowlist onConfirm={() => setStep(nextStep)} />
  }
  if (step === 'authorize-ach-transfer') {
    return <AuthorizeACHTransfer onConfirm={() => setStep('confirm-ach-mandate')} />
  }
  if (step === 'confirm-ach-mandate') {
    return <ACHMandate onAccept={onAccept} type={type} />
  }
  return <ACHMandate onAccept={onAccept} type={type} />
}

type ConfirmACHCheckboxProps = {
  checked: boolean
  onChange: () => void
  label: React.ReactNode
}
const ConfirmACHCheckbox = ({ checked, onChange, label }: ConfirmACHCheckboxProps) => (
  <ListItem disablePadding disableGutters>
    <Checkbox label={label} checked={checked} onChange={onChange} required />
  </ListItem>
)

const ConfirmACHAllowlist = ({ onConfirm }: { onConfirm: () => void }) => {
  const [isChecked, setIsChecked] = React.useState(false)

  return (
    <div>
      <Typography variant="body2" gutterBottom>
        You must confirm that the following ACH IDs are permitted to initiate ACH transfers from the specified
        bank account.
      </Typography>
      <Typography variant="body2" gutterBottom>
        For more details, please read{' '}
        <ExternalLink
          href="https://docs.google.com/document/d/1Bab4yKF3mzIdldTAur4jvJObwy7IXX6UP9rB_IMsw2g/edit?tab=t.0"
          target="_blank"
        >
          Whitelisting Alice&apos;s ACH IDs (Payee IDs)
        </ExternalLink>
        . You may wish to forward this document to your finance team.
      </Typography>
      <Typography variant="body2" component="ul" gutterBottom>
        {ACH_ALLOWLIST.map((achNumber) => (
          <li key={achNumber}>
            <strong>{achNumber}</strong>
          </li>
        ))}
      </Typography>

      <List>
        <ConfirmACHCheckbox
          checked={isChecked}
          onChange={() => setIsChecked(!isChecked)}
          label="ACH transfers initiated by these ACH IDs are authorized."
        />
      </List>

      <ActionLayout
        disablePadding
        primary={
          <Button variant="contained" onClick={onConfirm} disabled={!isChecked} fullWidth>
            Confirm
          </Button>
        }
      />
    </div>
  )
}

const AuthorizeACHTransfer = ({ onConfirm }: { onConfirm: () => void }) => {
  return (
    <div>
      <Typography variant="body2" gutterBottom>
        A <strong>$100 Reserve Deposit</strong> will be transferred to open and fund your organization&apos;s
        Contribution&apos;s Account.
      </Typography>
      <Typography variant="body2" gutterBottom>
        This amount is owned by your organization, is not a fee to Alice, and is not a payment by your
        organization for your employees pretax expenses.
      </Typography>
      <Typography variant="body2" gutterBottom>
        This Initial Reserve Deposit and any future per employee Reserve Deposits will be returned to your
        organization at the conclusion of the service term and when enrolled employees are terminated,
        respectively.
      </Typography>
      <ActionLayout
        disablePadding
        primary={
          <Button variant="contained" onClick={onConfirm} fullWidth>
            Authorize transfer
          </Button>
        }
      />
    </div>
  )
}

export const ACHMandate = ({ onAccept, type }: { onAccept: () => void; type: 'billing' | 'banking' }) => (
  <>
    <Typography variant="h3" gutterBottom>
      Acknowledgements of Debits by Pretax Hero Inc. (dba Alice)
      {type === 'banking' && ' to your Treasury Financial Account (AKA Benefits Contribution Account)'}
    </Typography>
    <Typography variant="body2" component="p" gutterBottom>
      By clicking [Accept], you authorize Pretax Hero Inc. (dba Alice or This is Alice) to debit the specified
      bank account(s) for any transfers required arising from your use of Pretax Hero Inc.&apos;s services,
      pursuant to Pretax Hero Inc.&apos;s website and terms, until this authorization is revoked.
    </Typography>
    <Typography variant="body2" component="p" gutterBottom>
      You may amend or cancel this authorization at any time by providing notice to Pretax Hero Inc. with 30
      (thirty) days notice.
    </Typography>
    <Typography variant="body2" component="p">
      If you use additional Pretax Hero Inc. services periodically pursuant to Pretax Hero Inc.&apos;s terms,
      you authorize Pretax Hero Inc. to debit your bank account periodically for those services. Payments that
      fall outside of the regular debits authorized above will only be debited after your authorization is
      obtained.
    </Typography>
    <ActionLayout
      disablePadding
      primary={
        <Button variant="contained" onClick={onAccept}>
          Accept
        </Button>
      }
    />
  </>
)
